import React, { useState, useEffect } from 'react';
import './GrabTopline.css';
import Navbar from '../components/Navbar';
import { useCountry } from '../contexts/CountryContext';
import { useData } from '../contexts/dataContext';
import config from '../config'; // Adjust the import path as necessary

const GrabTopline = ({ project, onNavigation }) => {
  const { selectedCountry, countryName, availableWaves, setAvailableWaves, availableBrands, setAvailableBrands, availableCities, setAvailableCities, filters, setFilters, handleFilterChange, globalFilter, setGlobalFilter } = useCountry();  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { sampleBrands, logoMap } = useData();
  
  // Ensure filters have proper defaults
  const safeFilters = {
    brands: filters.brands || [],
    city: filters.city || [],
    wave: filters?.wave || '',
    demographic: filters?.demographic || ''
  };

  const handleWaveChange = (wave) => {
    handleFilterChange({ ...safeFilters, wave });
  };

  const handleCityToggle = (city) => {
    const newCity = safeFilters.city === city ? '' : city;
    handleFilterChange({ ...safeFilters, city: newCity });
  };

  useEffect(() => {
    setGlobalFilter('GRABTOPLINE');
  }, []);

  // Process and categorize the data
  useEffect(() => {
    if (sampleBrands && sampleBrands.length > 0) {
      // Find GrabFood data
      const grabFoodData = sampleBrands.find(brand => brand.name === "GrabFood");
      if (grabFoodData) {
        setData(grabFoodData);
      }
      setLoading(false);
    }
  }, [sampleBrands]);

  // Categorize the data
  const categorizeData = () => {
    if (!data || !data.funnelData) return {};
    
    const categories = {
      funnel: [],
      reasons: [],
      barriers: [],
      imagery: [],
      nps: []
    };
    
    data.funnelData.forEach(item => {
      if (item.catlabel === "Brand Funnel") {
        categories.funnel.push(item);
      } else if (item.catlabel === "Reason to use") {
        categories.reasons.push(item);
      } else if (item.catlabel === "Barriers to use") {
        categories.barriers.push(item);
      } else if (item.catlabel.includes("Brand Imagery")) {
        categories.imagery.push(item);
      } else if (item.catlabel.includes("NPS")) {
        categories.nps.push(item);
      }
    });
    
    // Sort reasons and barriers by percentage
    categories.reasons.sort((a, b) => b.percentage - a.percentage);
    categories.barriers.sort((a, b) => b.percentage - a.percentage);
    
    return categories;
  };

  const categories = categorizeData();

  // Fetch filter values whenever selectedCountry changes
  useEffect(() => {
    const fetchFilterValues = async () => {
      if (!selectedCountry) {
        setAvailableBrands([]);
        setAvailableCities([]);
        setAvailableWaves([]);
        return;
      }

      try {
        setLoading(true);
        const baseUrl = `${config.API_BASE_URL}`;
        
        const params = new URLSearchParams();
        if (selectedCountry && selectedCountry !== 'All') {
          params.append('country', countryName);
        }
        params.append('category', globalFilter);
        const queryString = params.toString();
        const urlSuffix = queryString ? `?${queryString}` : '';

        const brandsResponse = await fetch(`${baseUrl}/api/unique-brands${urlSuffix}`);
        const brandsData = await brandsResponse.json();
        
        const citiesResponse = await fetch(`${baseUrl}/api/unique-cities${urlSuffix}`);
        const citiesData = await citiesResponse.json();
        
        const wavesResponse = await fetch(`${baseUrl}/api/unique-waves${urlSuffix}`);
        const wavesData = await wavesResponse.json();

        if (brandsResponse.ok && citiesResponse.ok && wavesResponse.ok) {
          setAvailableBrands(brandsData.brands || []);
          setAvailableCities(citiesData.cities || []);
          setAvailableWaves(wavesData.waves || []);
          setError(null);
        } else {
          throw new Error('Failed to fetch filter values');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching filter values:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFilterValues();
  }, [selectedCountry]);

  // Render brand funnel cards
  const renderFunnelCards = () => {
    if (!categories.funnel || categories.funnel.length === 0) return null;
    
    return (
      <div className="funnel-cards">
        <h2 className="section-title">Brand Funnel</h2>
        <div className="cards-grid">
          {categories.funnel.map((item, index) => (
            <div key={index} className="funnel-card">
              <div className="funnel-value">{item.percentage.toFixed(2)}%</div>
              <div className="funnel-label">{item.label}</div>
              <div className="funnel-base">Base: {item.Base}</div>
              <div 
                className="funnel-progress" 
                style={{ 
                  background: `linear-gradient(to right, ${data.color || '#4CAF50'} ${item.percentage}%, #f0f0f0 ${item.percentage}%)`
                }}
              ></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render reasons to use as horizontal bars
  const renderReasons = () => {
    if (!categories.reasons || categories.reasons.length === 0) return null;
    
    return (
      <div className="reasons-section">
        <h2 className="section-title">Reasons to Use</h2>
        <div className="bars-container">
          {categories.reasons.map((item, index) => (
            <div key={index} className="bar-item">
              <div className="bar-label">{item.label}</div>
              <div className="bar-wrapper">
                <div 
                  className="bar-fill" 
                  style={{ 
                    width: `${item.percentage}%`,
                    backgroundColor: data.color || '#4CAF50'
                  }}
                >
                  <span className="bar-value">{item.percentage.toFixed(2)}%</span>
                </div>
              </div>
              <div className="bar-base">Base: {item.Base}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render barriers to use as horizontal bars
  const renderBarriers = () => {
    if (!categories.barriers || categories.barriers.length === 0) return null;
    
    return (
      <div className="barriers-section">
        <h2 className="section-title">Barriers to Use</h2>
        <div className="bars-container">
          {categories.barriers.map((item, index) => (
            <div key={index} className="bar-item">
              <div className="bar-label">{item.label}</div>
              <div className="bar-wrapper">
                <div 
                  className="bar-fill" 
                  style={{ 
                    width: `${item.percentage}%`,
                    backgroundColor: '#F44336'
                  }}
                >
                  <span className="bar-value">{item.percentage.toFixed(2)}%</span>
                </div>
              </div>
              <div className="bar-base">Base: {item.Base}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render brand imagery as 6 stacked bars in one chart
const renderBrandImagery = () => {
  if (!categories.imagery || categories.imagery.length === 0) return null;
  
  // Group imagery items by question and calculate top 2 box
  const imageryGroups = {};
  categories.imagery.forEach(item => {
    const question = item.catlabel.split(':')[1]?.trim() || item.catlabel;
    if (!imageryGroups[question]) {
      imageryGroups[question] = {
        items: [],
        top2Box: 0
      };
    }
    
    // Add to items array
    imageryGroups[question].items.push(item);
    
    // Calculate top 2 box (assuming 4-5 on a 5-point scale)
    if (item.label.includes('4') || item.label.includes('5') || 
        item.label.includes('Strongly agree') || item.label.includes('Somewhat agree')) {
      imageryGroups[question].top2Box += item.percentage;
    }
  });
  
  // Define the desired order for labels
  const labelOrder = {
    'Strongly agree': 1,
    'Somewhat agree': 2,
    'Neutral': 3,
    'Somewhat disagree': 4,
    'Strongly disagree': 5,
    '5': 1,
    '4': 2,
    '3': 3,
    '2': 4,
    '1': 5
  };
  
  return (
    <div className="imagery-section">
      <h2 className="section-title">Brand Imagery</h2>
      <div className="imagery-chart-container">
        {Object.entries(imageryGroups).map(([question, groupData], index) => {
          // Sort items by the defined order
          const sortedItems = [...groupData.items].sort((a, b) => {
            const aLabel = a.label.split(' - ')[1] || a.label;
            const bLabel = b.label.split(' - ')[1] || b.label;
            
            // Get order values, default to 99 if not found
            const aOrder = labelOrder[aLabel] || 99;
            const bOrder = labelOrder[bLabel] || 99;
            
            return aOrder - bOrder;
          });
          
          return (
            <div key={index} className="imagery-chart">
              <div className="imagery-question">{question}</div>
              <div className="imagery-top2box">Top 2 Box: {groupData.top2Box.toFixed(2)}%</div>
              <div className="stacked-bars-horizontal">
                {sortedItems.map((item, idx) => (
                  <div key={idx} className="stacked-bar-horizontal">
                    <div className="stacked-label-small">{item.label.split(' - ')[1] || item.label}</div>
                    <div className="stacked-bar-wrapper-horizontal">
                      <div 
                        className="stacked-bar-horizontal-fill" 
                        style={{ 
                          width: `${item.percentage}%`,
                          backgroundColor: data.color || '#4CAF50'
                        }}
                      ></div>
                      <span className="stacked-value-small">{item.percentage.toFixed(2)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
  // Render NPS as a doughnut chart
  const renderNPS = () => {
    if (!categories.nps || categories.nps.length === 0) return null;
    
    const npsScore = categories.nps.find(item => item.label === "NPS")?.percentage || 0;
    const promoters = categories.nps.find(item => item.label === "Promoters")?.percentage || 0;
    const detractors = categories.nps.find(item => item.label === "Detractor")?.percentage || 0;
    const passives = 100 - promoters - detractors;
    
    // Calculate angles for doughnut chart
    const promoterAngle = (promoters / 100) * 360;
    const passiveAngle = (passives / 100) * 360;
    const detractorAngle = (detractors / 100) * 360;
    
    return (
      <div className="nps-section">
        <h2 className="section-title">Net Promoter Score (NPS)</h2>
        <div className="nps-container-doughnut">
          <div className="nps-doughnut-chart">
            <div className="doughnut">
              <div 
                className="doughnut-segment promoters" 
                style={{ 
                  transform: `rotate(${promoterAngle / 2}deg)`,
                  '--value': promoterAngle
                }}
              ></div>
              <div 
                className="doughnut-segment passives" 
                style={{ 
                  transform: `rotate(${promoterAngle + passiveAngle / 2}deg)`,
                  '--value': passiveAngle
                }}
              ></div>
              <div 
                className="doughnut-segment detractors" 
                style={{ 
                  transform: `rotate(${promoterAngle + passiveAngle + detractorAngle / 2}deg)`,
                  '--value': detractorAngle
                }}
              ></div>
              <div className="doughnut-center">
                <div className="nps-score-doughnut">{npsScore >= 0 ? '+' : ''}{npsScore.toFixed(2)}</div>
                <div className="nps-label-doughnut">NPS</div>
              </div>
            </div>
          </div>
          
          <div className="nps-legend">
            <div className="nps-legend-item">
              <div className="legend-color promoters"></div>
              <div className="legend-label">Promoters: {promoters.toFixed(2)}%</div>
            </div>
            <div className="nps-legend-item">
              <div className="legend-color passives"></div>
              <div className="legend-label">Passives: {passives.toFixed(2)}%</div>
            </div>
            <div className="nps-legend-item">
              <div className="legend-color detractors"></div>
              <div className="legend-label">Detractors: {detractors.toFixed(2)}%</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grab-topline-container">
      <Navbar onNavigation={onNavigation} />
      
      {/* Header section with title and filters */}
      <div className="topline-header">
        <div className="header-title">
          <h1>Grab Topline</h1>
          {selectedCountry && <span className="country-badge">{selectedCountry}</span>}
        </div>
        
        <div className="header-filters">
          {availableCities && availableCities.length > 1 && (
            <div className="filter-group">
              <label htmlFor="city-filter">City</label>
              <select 
                id="city-filter"
                value={safeFilters.city || 'All'} 
                onChange={(e) => handleCityToggle(e.target.value)}
                className="filter-select"
                disabled={!selectedCountry || availableCities.length === 0}
              >
                {availableCities.map(city => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="filter-group">
            <label htmlFor="wave-filter">Wave</label>
            <select 
              id="wave-filter"
              value={safeFilters.wave} 
              onChange={(e) => handleWaveChange(e.target.value)}
              className="filter-select"
              disabled={!selectedCountry}
            >
              
              {availableWaves.map(wave => (
                <option key={wave} value={wave}>
                  {`${wave}`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="topline-content">
        {loading ? (
          <div className="loading">Loading data...</div>
        ) : error ? (
          <div className="error">
            <p>Error loading data: {error}</p>
          </div>
        ) : data ? (
          <div className="data-visualization">
            {renderFunnelCards()}
            {renderNPS()}
            {/* Side-by-side Reasons and Barriers */}
            <div className="reasons-barriers-container">
              <div className="reasons-column">
                {renderReasons()}
              </div>
              <div className="barriers-column">
                {renderBarriers()}
              </div>
            </div>
            
            {renderBrandImagery()}
            
          </div>
        ) : (
          <div className="no-data">No data available for GrabFood</div>
        )}
      </div>
    </div>
  );
};

export default GrabTopline;